import i18n from "@/i18n";
import {
	API_ERROR_CODES,
	ApiCallError,
	impactFromError,
	referencesFromError,
	type InUseReference,
} from "@/api";
import { seatUnavailableFromError, seatUnavailableMessage } from "@/shared/utils/seats";

/** The message of each refusal that carries a list of places, by `code`. */
const IN_USE_MESSAGES: Record<string, string> = {
	[API_ERROR_CODES.attributeInUse]: "common.errors.attributeInUse",
	[API_ERROR_CODES.typeInUse]: "common.errors.typeInUse",
	[API_ERROR_CODES.typeValueInUse]: "common.errors.typeValueInUse",
	[API_ERROR_CODES.processDataobjectInUseByAction]:
		"common.errors.processDataobjectInUseByAction",
};

/**
 * Display message for a caught value.
 *
 * `catch` binds `unknown`, so every call site would otherwise have to reckon
 * with "this might not be an Error" itself — which is what the `catch (e: any)`
 * + `e.message ?? String(e)` pattern was papering over. The duck-typed branch is
 * deliberate: it keeps the old behaviour for rejections that carry a `message`
 * without extending Error.
 *
 * REFUSALS ARE TRANSLATED, by their stable `code` (never the message) — among them a write on an entity
 * that lives in a dependency, and any write refused because a moved `wip` dependency is held for
 * review. The UI blocks the first BEFORE the request, so reaching the server with one means a
 * control slipped through — and either way the server's own wording is English prose naming
 * generator internals, where a designer needs to know what to do. Done here, in the one place every
 * failed write already goes through, so no surface has to remember it.
 *
 * `dependency_impact_rejected` WITH a report is a refused pin change, whose dialog shows the
 * report; only the bare one, which a held `wip` move produces, is translated.
 *
 * A THIRD is a write refused for want of a seat (`seat_unavailable`): creating a process and every
 * way of taking part in one — an answer, an attribute, an embedded create or remove — needs a seat
 * in a department's dataspace, and the server's English does not say how many there are or that
 * waiting helps. Said here with the department and the number from `details`, so every one of those
 * writes' toasts and banners words it alike.
 *
 * A FOURTH, A FIFTH and A SIXTH are a delete of a dataobject, a relation or a process that an action
 * still names (`dataobject_in_use_by_action`, `relation_in_use_by_action`,
 * `process_in_use_by_action`): the dialog that asked says so, and with the server's naming of the
 * action as the reason.
 *
 * A SEVENTH is the remove of an attribute, a type, a type value or a process entry that the model
 * still names (`attribute_in_use`, `type_in_use`, `type_value_in_use`,
 * `process_dataobject_in_use_by_action`), which comes with the places in `details.references`.
 *
 * AN EIGHTH is an alias refused (`invalid_alias`, `alias_taken`), whatever it was sent for.
 */
export function errorMessage(e: unknown): string {
	if (e instanceof ApiCallError) {
		if (e.code === API_ERROR_CODES.dependencyEntityProtected) {
			return i18n.global.t("common.errors.dependencyProtected");
		}
		if (e.code === API_ERROR_CODES.predefinedDataobjectProtected) {
			return i18n.global.t("common.errors.predefinedProtected");
		}
		// An interactable its model bound to other people: answered, pressed or closed by somebody else.
		if (e.code === API_ERROR_CODES.notAnActor) {
			return i18n.global.t("common.errors.notAnActor");
		}
		if (e.code === API_ERROR_CODES.userNotInDataspace) {
			return i18n.global.t("common.errors.userNotInDataspace");
		}
		// A delete the Designer predicted would go through: its own check fell behind the generator.
		if (e.code === API_ERROR_CODES.dataobjectInUse) {
			return i18n.global.t("common.errors.dataobjectInUse");
		}
		if (e.code === API_ERROR_CODES.attributeInUseByAxiom) {
			return i18n.global.t("common.errors.attributeInUseByAxiom");
		}
		if (e.code === API_ERROR_CODES.actionManagedByDependency) {
			return i18n.global.t("common.errors.actionManaged");
		}
		if (e.code === API_ERROR_CODES.dependencyImpactRejected && !impactFromError(e)) {
			return i18n.global.t("common.errors.dependencyHeld");
		}
		// The Designer shows such a project's read-only home instead (`useImportedProjects`); this
		// is for wherever else one surfaces.
		if (e.code === API_ERROR_CODES.projectImported) {
			return i18n.global.t("common.errors.projectImported");
		}
		// A delete refused because an action still names the dataobject or the relation. Told by the
		// code; the server's own sentence, which names the activity and the action, is what the
		// message carries as its reason — it is shown, never read.
		if (e.code === API_ERROR_CODES.dataobjectInUseByAction) {
			return i18n.global.t("common.errors.dataobjectInUseByAction", { reason: e.message });
		}
		if (e.code === API_ERROR_CODES.relationInUseByAction) {
			return i18n.global.t("common.errors.relationInUseByAction", { reason: e.message });
		}
		if (e.code === API_ERROR_CODES.processInUseByAction) {
			return i18n.global.t("common.errors.processInUseByAction", { reason: e.message });
		}
		// The four removes the model still needs. The dialog or banner that asked shows the places, as
		// links; this says what was refused, and the server's own sentence follows only when there is
		// no list to show (a server that sends none), so the places are not said twice.
		const inUse = IN_USE_MESSAGES[e.code ?? ""];
		if (inUse) {
			const said = i18n.global.t(inUse);
			return referencesFromError(e).length ? said : `${said} ${e.message}`;
		}
		// An alias refused: the field that sent it says so under itself too, but a toast or a banner
		// says it in words, and never which dataspace has it — the server does not say.
		if (e.code === API_ERROR_CODES.invalidAlias) {
			return i18n.global.t("common.errors.invalidAlias");
		}
		if (e.code === API_ERROR_CODES.aliasTaken) {
			return i18n.global.t("common.errors.aliasTaken");
		}
		const noSeat = seatUnavailableFromError(e);
		if (noSeat) return seatUnavailableMessage(noSeat);
	}
	if (e instanceof Error) return e.message;
	if (typeof e === "object" && e !== null && "message" in e) {
		const message = (e as { message?: unknown }).message;
		if (typeof message === "string") return message;
	}
	return String(e);
}

/**
 * A failed write as a store keeps it for the view that shows it: the message, and — for the four
 * removes the model refuses while something still uses what they would take — the places that do,
 * which the view lists as links. `references` is absent, not empty, when there are none, so what a
 * store holds for every other failure is as it was.
 */
export function writeFailure(e: unknown): { message: string; references?: InUseReference[] } {
	const references = referencesFromError(e);
	return { message: errorMessage(e), ...(references.length ? { references } : {}) };
}
